import { useMemo } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import type { ColumnType } from "@similarweb/pro-logic-components";
import { TextCell, AreaLightGraphCell, ChangeCell } from "@similarweb/pro-logic-components";

import { NO_DATA_SIGN } from "app/filters/const";
import { useTranslation } from "pro-features/components/WithTranslation";

import { OtherCompetitorsCell } from "../../../components/common/OtherCompetitorsCell";
import WebsiteWithFaviconCell from "../../../components/Share/WebsiteWithFaviconCell/WebsiteWithFaviconCell";
import { formatChangeValue } from "../../../helpers/formatChangeValue";
import { BenchmarkGranularity } from "../../../services/api/constants";
import type { BenchmarkUrlParams } from "../../../types/paramsTypes";
import { useAllCompetitorsModal } from "../components/AllCompetitorsModalContext";
import type { ITransformedDomain } from "../utils/transformData";

import { useTrendCellConfig } from "./useTrendCellConfig";

export interface ICommonData {
    changePoP?: number | null;
    changeYoY?: number | null;
    rank?: number;
    trend?: [number, number][];
}

export interface IGroupedDomainData {
    domain: ITransformedDomain | ITransformedDomain[];
}

export interface IDomainData {
    domain: ITransformedDomain;
}

export const useCommonColumnsConfig = () => {
    const translate = useTranslation();
    const trendCellConfig = useTrendCellConfig();
    const {
        params: { granularity },
    } = useCurrentStateAndParams<BenchmarkUrlParams>();
    const { openModal } = useAllCompetitorsModal();

    return useMemo(() => {
        const commonDomainColumnParams = {
            title: translate("common.domain"),
            dataIndex: "domain" as const,
            minWidth: 180,
        };

        const groupedDomainColumn = {
            ...commonDomainColumnParams,
            render: (domainData: ITransformedDomain | ITransformedDomain[]) => {
                if (!Array.isArray(domainData)) {
                    return (
                        <WebsiteWithFaviconCell
                            value={domainData.value}
                            row={{ isPrimary: domainData.isPrimaryDomain, defaultIcon: domainData.icon }}
                            showTooltip={false}
                            metricName="marketingChannels"
                        />
                    );
                }

                return (
                    <OtherCompetitorsCell onClick={openModal}>
                        {translate("bt.report.channels.traffic-share-table.other-competitors", {
                            count: domainData.length,
                        })}
                    </OtherCompetitorsCell>
                );
            },
        } satisfies ColumnType<IGroupedDomainData>;

        const highlightedDomainColumn = {
            ...commonDomainColumnParams,
            render: (domainData: ITransformedDomain) => (
                <WebsiteWithFaviconCell
                    value={domainData.value}
                    row={{ isPrimary: domainData.isPrimaryDomain, defaultIcon: domainData.icon }}
                    showTooltip={false}
                    metricName="marketingChannels"
                    primaryIcon="checked-circle"
                />
            ),
        } satisfies ColumnType<IDomainData>;

        const rankColumn = {
            align: "center",
            dataIndex: "rank",
            render: (value) => <TextCell>{value ? `#${value}` : NO_DATA_SIGN}</TextCell>,
            title: translate("common.tables.columns.rank.title"),
            width: 56,
            minWidth: 56,
        } satisfies ColumnType<ICommonData>;

        const trendColumn = {
            align: "right",
            dataIndex: "trend",
            render: (value: [number, number][] | undefined) => (
                <AreaLightGraphCell
                    data={value ? [{ data: value }] : undefined}
                    config={trendCellConfig}
                />
            ),
            title: translate("bt.report.channels.traffic-share-table.trend-column.title"),
            width: 180,
            minWidth: 180,
        } satisfies ColumnType<ICommonData>;

        const changeColumn = {
            align: "right" as const,
            render: (value: number | null | undefined) => (
                <ChangeCell
                    value={value ?? undefined}
                    formattedValue={value != null ? formatChangeValue(value) : undefined}
                    noChangeSign="0pp"
                />
            ),
            width: 180,
            minWidth: 180,
        };

        const popColumn = {
            ...changeColumn,
            dataIndex: "changePoP",
            title: translate(granularity === BenchmarkGranularity.MONTHLY ? "global.change.mom" : "global.change.wow"),
        } satisfies ColumnType<ICommonData>;

        const yoyColumn = {
            ...changeColumn,
            dataIndex: "changeYoY",
            title: translate("common.yoy.change"),
        } satisfies ColumnType<ICommonData>;

        return {
            groupedDomainColumn,
            highlightedDomainColumn,
            popColumn,
            rankColumn,
            trendColumn,
            yoyColumn,
        };
    }, [translate, trendCellConfig, granularity, openModal]);
};
